import { useState, useEffect } from "react";
import { EditorState, Modifier } from "draft-js";
import {
  getSelectionText,
  getEntityRange,
  getSelectionEntity,
} from "draftjs-utils";
import linkifyIt from "linkify-it";
import IconButton from "@mui/material/IconButton";
// import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";

import UIDialog from "../../../dialog";
import { stopPropagation } from "./../../utils/common";
import Icon from "./../../../icon";
import Input from "../../../input";
import Button from "../../../button";
import NodeType from "../../../node-type";

const linkify = linkifyIt();
const linkifyLink = (params: { target: string }) => {
  const links = linkify.match(params.target);
  return {
    ...params,
    target: (links && links[0] && links[0].url) || params.target,
  };
};

const Link = (props: any) => {
  const { config, editorState } = props;
  const { options, link } = config;
  const [currentEntity, setCurrentEntity] = useState(
    editorState ? getSelectionEntity(editorState) : undefined
  );
  const [showModal, setShowModal] = useState(false);
  const [linkTarget, setLinkTarget] = useState("");
  const [linkTargetOption, setLinkTargetOption] = useState("_blank");
  const [linkTitle, setLinkTitle] = useState("");
  const [showInvalidUrl, setShowInvalidUrl] = useState(false);
  const [showEmptyText, setShowEmptyText] = useState(false);

  const [selectedTextContent, setSelectedTextContent] = useState<string>("");

  useEffect(() => {
    setCurrentEntity(getSelectionEntity(editorState));
  }, [editorState]);

  const forceExpandAndShowModal = () => {
    // @ts-ignore
    const { link, selectionText } = getCurrentValues();
    // Save the selected text content
    setSelectedTextContent(selectionText || "");
    setShowModal(true);
    setLinkTarget(link && link.target);
    setLinkTargetOption((link && link.targetOption) || linkTargetOption);
    setLinkTitle((link && link.title) || selectionText);
  };

  const getCurrentValues = () => {
    const { editorState } = props;
    const contentState = editorState.getCurrentContent();
    const currentValues: any = {};
    if (
      currentEntity &&
      contentState.getEntity(currentEntity).get("type") === "LINK"
    ) {
      currentValues.link = {};
      const entityRange =
        currentEntity && getEntityRange(editorState, currentEntity);
      currentValues.link.target =
        currentEntity && contentState.getEntity(currentEntity).get("data").url;
      currentValues.link.targetOption =
        currentEntity &&
        contentState.getEntity(currentEntity).get("data").targetOption;
      currentValues.link.title = entityRange && entityRange.text;
    }
    currentValues.selectionText = getSelectionText(editorState);
    return currentValues;
  };

  const isValidUrl = (url: string) => {
    try {
      new URL(url.startsWith('http') ? url : `https://${url}`);
      return true;
    } catch {
      return false;
    }
  };
  
  const isValidLinkText = (value: string) => {
    return value.trim().length > 0; 
  };
  const addLink = () => {
    if (!linkTarget || !isValidUrl(linkTarget)) {
    setShowInvalidUrl(true);
    return; 
    }
    if (!linkTitle.trim()) {
      setShowEmptyText(true);
      return;
    }
    setShowInvalidUrl(false);
    setShowEmptyText(false);
    const {
      config: { linkCallback },
    } = props;
    const { editorState, onChange } = props;
    const linkifyCallback = linkCallback || linkifyLink;
    const linkified = linkifyCallback({
      title: linkTitle,
      target: linkTarget,
      targetOption: linkTargetOption,
    });

    let selection = editorState.getSelection();
    // If no text is currently selected but we have saved text, try to find and select it
    if (selection.isCollapsed() && selectedTextContent && linkTitle === selectedTextContent) {
      const currentContent = editorState.getCurrentContent();
      const currentBlock = currentContent.getBlockForKey(selection.getAnchorKey());
      const blockText = currentBlock.getText();
      const textIndex = blockText.indexOf(selectedTextContent);
      if (textIndex >= 0) {
        selection = selection.merge({
          anchorOffset: textIndex,
          focusOffset: textIndex + selectedTextContent.length,
        });
      }
    }
    if (currentEntity) {
      const entityRange = getEntityRange(editorState, currentEntity);
      const isBackward = selection.getIsBackward();
      if (isBackward) {
        selection = selection.merge({
          anchorOffset: entityRange.end,
          focusOffset: entityRange.start,
        });
      } else {
        selection = selection.merge({
          anchorOffset: entityRange.start,
          focusOffset: entityRange.end,
        });
      }
    }
    const entityKey = editorState
      .getCurrentContent()
      .createEntity("LINK", "MUTABLE", {
        url: linkified.target,
        targetOption: linkified.targetOption,
      })
      .getLastCreatedEntityKey();

      let contentState;
      if (selection.isCollapsed()) {
        // If no text is selected, just insert the link text
        contentState = Modifier.insertText(
          editorState.getCurrentContent(),
          selection,
          `${linkified.title}`,
          editorState.getCurrentInlineStyle(),
          entityKey
        );
      } else {
        // If text is selected, replace it with the link
        contentState = Modifier.replaceText(
          editorState.getCurrentContent(),
          selection,
          `${linkified.title}`,
          editorState.getCurrentInlineStyle(),
          entityKey
        );
      }
    let newEditorState = EditorState.push(
      editorState,
      contentState,
      "insert-characters"
    );

    // insert a blank space after link
 // insert a blank space after link - Fixed the selection calculation
 const currentSelection = newEditorState.getSelection();
 const newCursorPosition = currentSelection.getAnchorOffset();
 selection = currentSelection.merge({
   anchorOffset: newCursorPosition,
   focusOffset: newCursorPosition,
 });
    newEditorState = EditorState.acceptSelection(newEditorState, selection);
    contentState = Modifier.insertText(
      newEditorState.getCurrentContent(),
      selection,
      " ",
      newEditorState.getCurrentInlineStyle(),
      undefined
    );
    onChange(
      EditorState.push(newEditorState, contentState, "insert-characters")
    );

    setTimeout(() => {
      setShowModal(false);
      setSelectedTextContent(""); // Clear saved text
    });
  };

  const updateValue = (event: any) => {
    if (event.target.name === "linkTitle") {
      setLinkTitle(event.target.value);
      setShowEmptyText(!event.target.value);
    } else if (event.target.name === "linkTarget") {
      setLinkTarget(event.target.value);
      setShowInvalidUrl(!!event.target.value && !isValidUrl(event.target.value));
    }
  };

  const updateTargetOption = (event: { target: { checked: any } }) => {
    // @ts-ignore
    setLinkTargetOption(event?.target?.checked ? "_blank" : "_self");
  };

  const handleModalToggle = () => {
    setShowModal(false);
  };

  const renderAddLinkModal = () => {
    return (
      <UIDialog
        className="ignore-onClickOutside"
        open={showModal}
        onClose={handleModalToggle}
      >
        <UIDialog.Header component="div" sx={{ padding: 0 }}>
          <Stack direction="row" justifyContent="space-between">
            <NodeType text="Insert Link " icon="link" color="#9AC7FD" />
            <Box
              sx={{
                px: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconButton onClick={handleModalToggle}>
                <Icon icon="close" size={16} />
              </IconButton>
            </Box>
          </Stack>
        </UIDialog.Header>
        <UIDialog.Body sx={{ px: "16px", pb: "8px", minWidth: "420px" }}>
          <Box onClick={stopPropagation}>
            <Box sx={{ mb: "16px" }}>
              <Typography
                component="div"
                sx={{
                  fontWeight: 400,
                  fontSize: "12px",
                  lineHeight: "18px",
                  color: "#7E8392",
                }}
              >
                Text<sup style={{ color: "red" }}>*</sup>
                <Input
                  placeholder="link text"
                  id="link-text-input"
                  size="small"
                  name="linkTitle"
                  onChange={updateValue}
                  onBlur={updateValue}
                  value={linkTitle}
                />
              </Typography>
            </Box>
            <Box sx={{ mb: "16px" }}>
              <Typography
                component="div"
                sx={{
                  fontWeight: 400,
                  fontSize: "12px",
                  lineHeight: "18px",
                  color: "#7E8392",
                }}
              >
                {showEmptyText && (
                  <Typography
                    component="div"
                    sx={{ color: "#d32f2f", fontSize: "11px", mt: "4px" }}
                  >
                    Link text cannot be empty
                  </Typography>
                )}
                Insert Link<sup style={{ color: "red" }}>*</sup>
                <Input
                  placeholder="link"
                  id="link-target-input"
                  size="small"
                  name="linkTarget"
                  onChange={updateValue}
                  onBlur={updateValue}
                  value={linkTarget}
                />
                {showInvalidUrl && (
                  <Typography
                    component="div"
                    sx={{ color: "#d32f2f", fontSize: "11px", mt: "4px" }}
                  >
                    Invalid URL
                  </Typography>
                  )}
              </Typography>
            </Box>

            <FormControlLabel
              label="Open link in new window"
              control={
                <Checkbox
                  checked={linkTargetOption === "_blank"}
                  onChange={updateTargetOption}
                  value="_blank"
                />
              }
            />
          </Box>
        </UIDialog.Body>
        <UIDialog.Footer
          sx={{ justifyContent: "space-between", px: "16px", pb: "8px" }}
        >
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleModalToggle}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={addLink}
            disabled={!linkTarget || !isValidUrl(linkTarget) || !isValidLinkText(linkTitle)}
            startIcon={<Icon icon="save" size={16} />}
          >
            Save
          </Button>
        </UIDialog.Footer>
      </UIDialog>
    );
  };

  return (
    <>
      {options.indexOf("link") >= 0 && (
        <Box sx={{ position: "relative" }}>
          <IconButton
            onClick={forceExpandAndShowModal}
            title={link.title}
            sx={{
              p: "2px !important",
            }}
            size="small"
          >
            <Icon icon={link.icon} size={16} color="#7E8392" />
          </IconButton>
        </Box>
      )}
      {showModal ? renderAddLinkModal() : undefined}
    </>
  );
};

export default Link;
